import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Building2, Sparkles, Loader2, Users, Clapperboard } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function CompanyLandingPage() {
    const nav = useNavigate();
    const { login } = useAuth();
    const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", password: "", website: "", industry: "" });
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/company/register", form);
            const res = await login(form.email, form.password);
            if (res.ok) {
                toast.success("Willkommen! Dein Unternehmens-Account ist bereit.");
                nav("/company");
            } else {
                nav("/login");
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Registrierung fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const USPS = [
        { icon: Sparkles, title: "KI erstellt deine Stellenanzeige", text: "Kurz beschreiben, wen du suchst — die KI schreibt Titel, Beschreibung, Anforderungen und Benefits." },
        { icon: Clapperboard, title: "5-Sekunden-Job-Spots", text: "Aus jeder Stelle wird automatisch ein visueller Spot im Bewerber-Feed — wie ein Reel, nicht wie eine Anzeige." },
        { icon: Users, title: "KI-Recruiter", text: "Die KI zeigt dir anonymisiert die passendsten Kandidaten mit Match-Score, Stärken und Lücken." },
    ];

    return (
        <PublicLayout>
            <Seo
                title="Talente finden mit KI-Bewerberprofilen | Bewerbung24.ai für Unternehmen"
                description="Erstellen Sie Stellenanzeigen mit KI, erreichen Sie aktive Bewerber im Job-Feed und erhalten Sie anonymisierte Kandidaten-Rankings — DSGVO-konform."
                path="/unternehmen"
            />
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-start" data-testid="company-landing">
                <div>
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-[0.15em] uppercase bg-red-50 text-red-600 border border-red-100">
                        <Building2 size={12} /> Bewerbung24.ai Recruit
                    </span>
                    <h1 className="font-heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-slate-900">
                        Finde Bewerber,<br />bevor sie suchen.
                    </h1>
                    <p className="mt-5 text-slate-600 leading-relaxed max-w-lg">
                        Erstelle Stellenanzeigen in 30 Sekunden mit KI, erreiche Bewerber direkt im Job-Feed
                        und lass den KI-Recruiter die passendsten Kandidaten finden — DSGVO-konform und anonymisiert.
                    </p>
                    <div className="mt-8 space-y-5">
                        {USPS.map((u, i) => (
                            <div key={i} className="flex gap-3.5">
                                <span className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0"><u.icon size={18} /></span>
                                <div>
                                    <p className="font-bold text-slate-800 text-sm">{u.title}</p>
                                    <p className="text-sm text-slate-500 mt-0.5">{u.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <form onSubmit={submit} className="card-elev p-7 space-y-3.5" data-testid="company-register-form">
                    <p className="font-heading text-xl font-bold text-slate-900">Unternehmens-Account erstellen</p>
                    <p className="text-xs text-slate-500 -mt-2">Kostenlos starten — erste Stelle in 2 Minuten online.</p>
                    <input required value={form.company_name} onChange={set("company_name")} placeholder="Unternehmensname *" data-testid="company-name-input" className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-red-400 outline-none text-sm" />
                    <input required value={form.contact_name} onChange={set("contact_name")} placeholder="Ansprechpartner *" data-testid="company-contact-input" className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-red-400 outline-none text-sm" />
                    <input required type="email" value={form.email} onChange={set("email")} placeholder="Geschäftliche E-Mail *" data-testid="company-email-input" className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-red-400 outline-none text-sm" />
                    <input required type="password" minLength={8} value={form.password} onChange={set("password")} placeholder="Passwort (min. 8 Zeichen) *" data-testid="company-password-input" className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-red-400 outline-none text-sm" />
                    <div className="grid grid-cols-2 gap-3">
                        <input value={form.website} onChange={set("website")} placeholder="Website" className="px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-red-400 outline-none text-sm" />
                        <input value={form.industry} onChange={set("industry")} placeholder="Branche" className="px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-red-400 outline-none text-sm" />
                    </div>
                    <button type="submit" disabled={busy} data-testid="company-register-btn" className="w-full py-3 rounded-full font-semibold text-sm btn-primary-grad disabled:opacity-60 inline-flex items-center justify-center gap-2">
                        {busy ? <Loader2 size={15} className="animate-spin" /> : <Building2 size={15} />} Account erstellen
                    </button>
                    <p className="text-[11px] text-slate-400 text-center">
                        Bereits registriert? <Link to="/login" className="text-red-600 font-semibold">Anmelden</Link> · DSGVO-konform · Made in Switzerland
                    </p>
                </form>
            </div>
        </PublicLayout>
    );
}
